import { ChangeDetectionStrategy, Component, OnDestroy, OnInit } from '@angular/core';

import { Store } from '@ngrx/store';
import { BehaviorSubject, combineLatest, Observable, Subject, Subscription } from 'rxjs';
import { filter, map, switchMap, take } from 'rxjs/operators';
import { IconLibrary, SvgLibraryIcon } from '@finnairoyj/fcom-ui-styles/enums';

import { ConfigService, unsubscribe } from '@fcom/core/index';
import {
  Category,
  FinnairBoundItem,
  FinnairItineraryItemFlight,
  FinnairOrder,
  FinnairPassengerItem,
  FinnairServiceItem,
} from '@fcom/dapi/api/models';
import { BoundType } from '@fcom/dapi/index';
import { ToasterService } from '@fcom/common/services';
import { AncillaryLoaderStatus } from '@fcom/common-booking/modules/ancillaries/enums';
import { getServiceData } from '@fcom/common-booking/utils/ancillaries.utils';
import { isFlight } from '@fcom/common-booking/utils/common-booking.utils';
import { SeatMapStatus } from '@fcom/common/interfaces/seat-map.interface';
import { AppConstants } from '@fcom/booking/app-constants';
import { GaContext, LoaderType } from '@fcom/common/interfaces';
import { CommonBookingFeatureState, OrderStatus, SeatMapActions, seatMapWithStatus } from '@fcom/common-booking/index';
import {
  ButtonLayout,
  ButtonMode,
  ButtonPosition,
  ButtonTheme,
  IconPosition,
  ModalButtons,
  NotificationLayout,
  NotificationTheme,
} from '@fcom/ui-components/index';

import { ManageBookingFeatureState } from '../../../interfaces';
import { SeatsUpdateProgress } from '../components/check-in-seatmap.component';
import { ManageBookingService } from '../../../services';
import { CheckInService } from '../../../services/check-in.service';

@Component({
  selector: 'fin-check-in-seatmap-route',
  templateUrl: 'check-in-seatmap-route.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CheckInSeatmapRouteComponent implements OnInit, OnDestroy {
  readonly ButtonTheme = ButtonTheme;
  readonly NotificationTheme = NotificationTheme;
  readonly IconPosition = IconPosition;
  readonly ButtonPosition = ButtonPosition;
  readonly NotificationLayout = NotificationLayout;
  readonly ButtonLayout = ButtonLayout;
  readonly OrderStatus = OrderStatus;
  readonly ModalButtons = ModalButtons;
  readonly LoaderType = LoaderType;
  readonly GaContext = GaContext;
  readonly ButtonMode = ButtonMode;
  readonly BoundType = BoundType;
  readonly SvgLibraryIcon = SvgLibraryIcon;
  readonly IconLibrary = IconLibrary;
  readonly C = AppConstants;

  order$: Observable<FinnairOrder>;
  orderStatus$: Observable<OrderStatus>;
  bound$: Observable<FinnairBoundItem>;
  seats$: Observable<FinnairServiceItem>;
  passengers$: Observable<FinnairPassengerItem[]>;
  locations$: Observable<FinnairOrder['locations']>;
  waitForSeatmap$: Subject<FinnairItineraryItemFlight> = new Subject<FinnairItineraryItemFlight>();
  showNoSeatsToReassignLeftNotification$: Observable<boolean>;
  seatsReassignmentSuccessful$: Observable<boolean>;
  updateServicesModalOn$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  ancillaryLoader$: BehaviorSubject<AncillaryLoaderStatus> = new BehaviorSubject<AncillaryLoaderStatus>(
    AncillaryLoaderStatus.OFF
  );
  ancillariesLoading$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  loading$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  checkoutUrl: string;

  private subscriptions = new Subscription();

  constructor(
    private store$: Store<CommonBookingFeatureState & ManageBookingFeatureState>,
    private toasterService: ToasterService,
    private configService: ConfigService,
    private manageBookingService: ManageBookingService,
    private checkInService: CheckInService
  ) {}

  ngOnInit(): void {
    this.checkoutUrl = this.configService.cfg.checkoutUrl;
    this.seatsReassignmentSuccessful$ = this.checkInService.getShowSeatsReassignmentSuccessfulMessage();
    this.subscriptions.add(
      this.seatsReassignmentSuccessful$
        .pipe(
          take(1),
          filter((seatsReassignmentSuccessful) => !!seatsReassignmentSuccessful)
        )
        .subscribe(() => {
          this.toasterService.addMessageToQueue({
            id: 'checkIn',
            theme: NotificationTheme.SUCCESS,
            keepMessageAfterNavigation: true,
            contentKey: 'MMB.checkIn.notifications.info.seatsReassigned',
          });
          this.checkInService.setShowSeatsReassignmentSuccessfulMessage(false);
        })
    );
    this.showNoSeatsToReassignLeftNotification$ = this.checkInService.getShowNoSeatsLeftNotificationMessage();
    this.orderStatus$ = this.checkInService.getOrderStatus();
    this.order$ = this.checkInService.getOrder();
    this.bound$ = this.checkInService.getBound();
    this.passengers$ = this.checkInService.getPassengers();
    this.locations$ = this.order$.pipe(map((order) => order.locations));
    this.seats$ = this.checkInService.getSeats();
  }

  ngOnDestroy(): void {
    unsubscribe(this.subscriptions);
  }

  goToNextStep(): void {
    this.checkInService.triggerNextCheckInStep();
  }

  onSeatsUpdate(seatsUpdateInformation: SeatsUpdateProgress): void {
    this.updateServicesModalOn$.next(seatsUpdateInformation.inProgress);
    if (seatsUpdateInformation.isUnsuccessful) {
      this.toasterService.addMessageToQueue({
        id: 'checkIn',
        theme: NotificationTheme.WARNING,
        keepMessageAfterNavigation: true,
        contentKey: 'MMB.checkIn.notifications.error.seatsUpdate',
      });
    }
  }

  checkInAssignSeatForFlight(event: { currentFlightId: string }): void {
    this.subscriptions.add(
      combineLatest([this.passengers$, this.order$])
        .pipe(take(1))
        .subscribe(([passengers, order]) => {
          const flight: FinnairItineraryItemFlight = order.bounds
            .flatMap((b) => b.itinerary)
            .filter(isFlight)
            .find((f) => f.id === event.currentFlightId);
          this.ancillaryLoader$.next(AncillaryLoaderStatus.REASSIGNING);
          this.ancillariesLoading$.next(true);
          passengers.forEach((p) => {
            this.store$.dispatch(
              SeatMapActions.setSeatForFlight({
                flightId: flight.id,
                travelerId: p.id,
                seat: {},
              })
            );
          });

          const [requests, messages] = getServiceData(this.store$, Category.SEAT, order.bounds, [flight]);
          this.manageBookingService.setServices(requests, Category.SEAT, {
            next: (finnairOrder) => {
              this.manageBookingService.triggerFetchSeatMap(
                flight,
                finnairOrder.id,
                finnairOrder.hash,
                finnairOrder.prices?.included?.total?.total?.totalAmount?.currencyCode ?? 'EUR'
              );
              this.waitForSeatmap$.next(flight);
            },
            error: () => {
              this.waitForSeatmap$.next(flight);
              this.toasterService.addMessageToQueue(messages.error);
            },
          });
        })
    );

    this.subscriptions.add(
      this.waitForSeatmap$
        .pipe(
          switchMap((flight) => this.store$.pipe(seatMapWithStatus(flight.id))),
          map((s) => s.status),
          filter((status) => status !== SeatMapStatus.PENDING && status !== SeatMapStatus.INITIAL),
          take(1)
        )
        .subscribe((_) => {
          this.ancillariesLoading$.next(false);
        })
    );
  }
}
